<script setup lang="ts">
import type { PlanName } from '~/composables/usePlanConfig'
import { LazyDowngradeSummaryModal } from '#components'

definePageMeta({
  layout: 'dashboard',
  middleware: ['username-required']
})

useSeoMeta({
  title: 'Billing',
  description: 'Manage your subscription and billing'
})

const authStore = useAuthStore()
const toast = useToast()
const route = useRoute()
const { trackGoal } = useDatafast()

const overlay = useOverlay()
const { currency, currencies, setCurrency } = useCurrency()
const runtimeConfig = useRuntimeConfig()
const paymentMode = computed(() => (runtimeConfig.public.paymentMode as 'onetime' | 'subscription') || 'onetime')
const isOnetime = computed(() => paymentMode.value === 'onetime')

const checkoutLoading = ref<string | null>(null)
const portalLoading = ref(false)
const downgradeLoading = ref(false)
const billingCycle = ref<'monthly' | 'yearly'>('monthly')

// Sync with Stripe on page visit to self-heal stale webhook data, then refresh.
// Lifetime users have no Stripe subscription to sync.
onMounted(async () => {
  if (!authStore.isLifetime) {
    try {
      await $fetch('/api/subscription/sync', { method: 'POST' })
    } catch {
      // Sync is best-effort - if it fails, we still show whatever the DB has
    }
  }
  authStore.fetchSubscription()
})

// Handle Stripe redirect query params
onMounted(() => {
  if (route.query.success === 'true') {
    toast.add({
      title: 'Subscription activated!',
      description: 'Welcome to your new plan. Enjoy your upgraded features.',
      icon: 'i-lucide-party-popper',
      color: 'success'
    })
    // Refresh subscription data
    authStore.fetchSubscription()
    // Clean up URL
    navigateTo('/dashboard/settings/billing', { replace: true })
  } else if (route.query.canceled === 'true') {
    trackGoal('checkout_cancelled', { type: 'plan' })
    toast.add({
      title: 'Checkout canceled',
      description: 'No charges were made. You can try again anytime.',
      icon: 'i-lucide-triangle-alert',
      color: 'warning'
    })
    navigateTo('/dashboard/settings/billing', { replace: true })
  }
})

// Plan display config
const planConfig = {
  free: {
    label: 'Free',
    color: 'neutral' as const,
    icon: 'i-lucide-user',
    description: 'The essentials to get started.'
  },
  pro: {
    label: 'Pro',
    color: 'fuchsia' as const,
    icon: 'i-lucide-zap',
    description: 'Unlock full creative control and make your page stand out.'
  },
  elite: {
    label: 'Elite',
    color: 'warning' as const,
    icon: 'i-lucide-crown',
    description: 'Maximum everything. No limits.'
  }
}

const currentPlanConfig = computed(() => planConfig[authStore.plan])

// Subscription status helpers
const statusLabel = computed(() => {
  const status = authStore.planStatus
  if (!status || authStore.plan === 'free') return null
  const map: Record<string, string> = {
    active: 'Active',
    trialing: 'Trial',
    past_due: 'Past Due',
    canceled: 'Canceled',
    incomplete: 'Incomplete'
  }
  return map[status] || status
})

const statusColor = computed(() => {
  const status = authStore.planStatus
  if (status === 'active') return 'success'
  if (status === 'trialing') return 'primary'
  if (status === 'past_due') return 'warning'
  if (status === 'canceled') return 'error'
  return 'neutral'
})

const trialDaysRemaining = computed(() => {
  if (authStore.planStatus !== 'trialing' || !authStore.currentPeriodEnd) return null
  const end = new Date(authStore.currentPeriodEnd)
  const now = new Date()
  const diff = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
  return Math.max(0, diff)
})

const periodEndFormatted = computed(() => {
  if (!authStore.currentPeriodEnd) return null
  return new Date(authStore.currentPeriodEnd).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })
})

const PLAN_RANK: Record<PlanName, number> = { free: 0, pro: 1, elite: 2 }

const plansForMode = computed(() => planCardsForMode(isOnetime.value))

function getPlanButtonLabel(plan: PlanName): string {
  if (plan === authStore.plan) return 'Current Plan'
  if (isOnetime.value && authStore.isLifetime && PLAN_RANK[plan] < PLAN_RANK[authStore.plan]) return 'Already Higher'
  if (PLAN_RANK[plan] < PLAN_RANK[authStore.plan]) return 'Downgrade'
  if (isOnetime.value && authStore.isLifetime && authStore.plan === 'pro' && plan === 'elite') return 'Upgrade to Elite'
  if (isOnetime.value) return 'Get Lifetime'
  if (plan === 'pro' && !authStore.hasUsedTrial) return 'Start 7-Day Free Trial'
  return 'Upgrade'
}

// Trial cancelled helper
const isTrialCancelled = computed(() => authStore.planStatus === 'trialing' && authStore.cancelAtPeriodEnd)

// Days until plan features stop (cancellation pending, not yet downgraded)
const planDaysRemaining = computed(() => {
  if (!authStore.cancelAtPeriodEnd || !authStore.currentPeriodEnd) return null
  if (authStore.downgradedAt) return null
  const end = new Date(authStore.currentPeriodEnd)
  const now = new Date()
  return Math.max(0, Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
})

// Days until file deletion (only AFTER subscription ended)
const graceDaysRemaining = computed(() => {
  if (!authStore.downgradedAt) return null
  const downgraded = new Date(authStore.downgradedAt)
  const deadline = new Date(downgraded.getTime() + 7 * 24 * 60 * 60 * 1000)
  const now = new Date()
  return Math.max(0, Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
})

async function openDowngradeDetails() {
  if (!authStore.userId) return
  try {
    const preview = await $fetch(`/api/character/${authStore.userId}/downgrade-preview`)
    if (graceDaysRemaining.value !== null) {
      preview.gracePeriodDays = graceDaysRemaining.value
    } else if (planDaysRemaining.value !== null) {
      preview.gracePeriodDays = planDaysRemaining.value + 7
    }
    // Sub already ended - features are already reset, only file deletions matter
    if (authStore.downgradedAt) {
      preview.featureChanges = []
    }
    const modal = overlay.create(LazyDowngradeSummaryModal)
    modal.open({ preview, mode: 'info' })
  } catch {
    toast.add({ title: 'Error', description: 'Failed to load downgrade details.', icon: 'i-lucide-x', color: 'error' })
  }
}

// Actions
async function handleCheckout(plan: PlanName) {
  // Allow re-subscribe even if same plan (when cancellation is pending or downgraded)
  const isCanceledOrDowngraded = authStore.cancelAtPeriodEnd || authStore.downgradedAt
  if (plan === authStore.plan && !isCanceledOrDowngraded) return

  // Lifetime plans cannot be downgraded - they're permanent
  if (authStore.isLifetime && PLAN_RANK[plan] < PLAN_RANK[authStore.plan]) {
    return
  }

  // Any downgrade → show preview first (but not if re-subscribing to same plan)
  if (PLAN_RANK[plan] < PLAN_RANK[authStore.plan] && plan !== authStore.plan) {
    await handleDowngrade(plan)
    return
  }

  checkoutLoading.value = plan
  try {
    // Active subscriber → redirect to Stripe Portal for plan changes.
    // Lifetime users skip the portal — they upgrade via a new lifetime checkout.
    const isActiveSub = authStore.subscription?.isActive && authStore.plan !== 'free' && !authStore.isLifetime
    if (isActiveSub) {
      const data = await $fetch<{ url: string }>('/api/stripe/portal', { method: 'POST' })
      if (data.url) {
        await navigateTo(data.url, { external: true })
      }
    } else {
      // No subscription, canceled, or lifetime upgrade → new checkout session
      trackGoal('checkout_started', {
        type: 'plan',
        plan,
        billing_cycle: isOnetime.value ? 'lifetime' : billingCycle.value,
        context: 'billing'
      })
      const data = await $fetch<{ url: string }>('/api/stripe/checkout', {
        method: 'POST',
        body: isOnetime.value
          ? { plan }
          : { plan, billingCycle: billingCycle.value }
      })
      if (data.url) {
        await navigateTo(data.url, { external: true })
      }
    }
  } catch (err: any) {
    toast.add({
      title: 'Error',
      description: err.data?.message || 'Failed to change plan. Please try again.',
      icon: 'i-lucide-x',
      color: 'error'
    })
  } finally {
    checkoutLoading.value = null
  }
}

async function handleDowngrade(targetPlan: PlanName = 'free') {
  downgradeLoading.value = true
  try {
    // Fetch preview of what will happen at the target plan
    const preview = await $fetch(`/api/character/${authStore.userId}/downgrade-preview?targetPlan=${targetPlan}`)

    // Show the summary modal
    const DowngradeSummaryModal = defineAsyncComponent(() =>
      import('~/components/modals/shared/DowngradeSummaryModal.vue')
    )
    const modal = overlay.create(DowngradeSummaryModal)
    const instance = modal.open({ preview })
    const confirmed = await instance.result

    if (!confirmed) return

    // Redirect to Stripe Portal for both cancellations and tier downgrades
    await handleManageSubscription()
  } catch (err: any) {
    toast.add({
      title: 'Error',
      description: err.data?.message || 'Failed to load downgrade preview.',
      icon: 'i-lucide-x',
      color: 'error'
    })
    checkoutLoading.value = null
  } finally {
    downgradeLoading.value = false
  }
}

async function handleManageSubscription() {
  portalLoading.value = true
  try {
    const data = await $fetch<{ url: string }>('/api/stripe/portal', {
      method: 'POST'
    })
    if (data.url) {
      await navigateTo(data.url, { external: true })
    }
  } catch (err: any) {
    toast.add({
      title: 'Error',
      description: err.data?.message || 'Failed to open billing portal.',
      icon: 'i-lucide-x',
      color: 'error'
    })
  } finally {
    portalLoading.value = false
  }
}
</script>

<template>
  <!-- Section header -->
  <UPageCard
    title="Billing"
    description="Manage your subscription plan and view usage."
    variant="naked"
    orientation="horizontal"
    class="mb-4"
  />

  <!-- Banner 1: Grace period ended - files being deleted -->
  <div
    v-if="graceDaysRemaining !== null && graceDaysRemaining === 0"
    class="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 rounded-xl border border-red-400/25 bg-red-500/10 mb-4"
  >
    <div class="flex items-start gap-3 flex-1">
      <UIcon
        name="i-lucide-alert-triangle"
        class="size-5 text-red-400 shrink-0 mt-0.5"
      />
      <p class="text-sm tint-red">
        Your grace period has ended. Over-limit files are being deleted.
        Re-subscribe to prevent further deletions.
      </p>
    </div>
    <div class="flex items-center gap-2 shrink-0">
      <UButton
        label="View Details"
        size="xs"
        color="neutral"
        variant="outline"
        icon="i-lucide-eye"
        @click="openDowngradeDetails"
      />
      <UButton
        label="Re-subscribe"
        size="xs"
        color="error"
        variant="solid"
        icon="i-lucide-arrow-up-circle"
        @click="handleCheckout((authStore.plan !== 'free' ? authStore.plan : (authStore.previousPlan as PlanName)) || 'pro')"
      />
    </div>
  </div>

  <!-- Banner 2: Subscription ended - grace period countdown -->
  <div
    v-else-if="graceDaysRemaining !== null && graceDaysRemaining > 0"
    class="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 rounded-xl border border-red-400/25 bg-red-500/10 mb-4"
  >
    <div class="flex items-start gap-3 flex-1">
      <UIcon
        name="i-lucide-alert-triangle"
        class="size-5 text-red-400 shrink-0 mt-0.5"
      />
      <p class="text-sm tint-red">
        Your subscription ended. You have <strong>{{ graceDaysRemaining }} day{{ graceDaysRemaining === 1 ? '' : 's' }}</strong>
        left before files exceeding Free plan limits are permanently deleted.
      </p>
    </div>
    <div class="flex items-center gap-2 shrink-0">
      <UButton
        label="View Details"
        size="xs"
        color="neutral"
        variant="outline"
        icon="i-lucide-eye"
        @click="openDowngradeDetails"
      />
      <UButton
        label="Re-subscribe"
        size="xs"
        color="error"
        variant="solid"
        icon="i-lucide-arrow-up-circle"
        @click="handleCheckout((authStore.plan !== 'free' ? authStore.plan : (authStore.previousPlan as PlanName)) || 'pro')"
      />
    </div>
  </div>

  <!-- Banner 3: Past due payment -->
  <div
    v-else-if="authStore.planStatus === 'past_due'"
    class="flex items-center gap-3 px-4 py-3 rounded-xl border border-red-400/25 bg-red-500/10 mb-4"
  >
    <UIcon
      name="i-lucide-alert-circle"
      class="size-5 text-red-400 shrink-0"
    />
    <p class="text-sm tint-red">
      Your payment failed. Please update your payment method to keep your plan.
    </p>
    <UButton
      label="Update Payment"
      size="xs"
      color="error"
      variant="soft"
      :loading="portalLoading"
      class="ml-auto shrink-0"
      @click="handleManageSubscription"
    />
  </div>

  <!-- Banner 4: Trial cancelled - won't charge -->
  <div
    v-else-if="trialDaysRemaining !== null && isTrialCancelled"
    class="flex items-center gap-3 px-4 py-3 rounded-xl border border-warning-400/25 bg-warning-500/10 mb-4"
  >
    <UIcon
      name="i-lucide-clock"
      class="size-5 text-warning-400 shrink-0"
    />
    <p class="text-sm text-warning-200">
      Your trial has been cancelled. Your {{ currentPlanConfig.label }} features will end
      <template v-if="trialDaysRemaining > 0">
        in <strong>{{ trialDaysRemaining }} day{{ trialDaysRemaining === 1 ? '' : 's' }}</strong>.
      </template>
      <template v-else>
        today.
      </template>
      You won't be charged.
    </p>
  </div>

  <!-- Banner 5: Trial active - will auto-charge -->
  <div
    v-else-if="trialDaysRemaining !== null"
    class="flex items-center gap-3 px-4 py-3 rounded-xl border border-primary-400/25 bg-primary-500/10 mb-4"
  >
    <UIcon
      name="i-lucide-clock"
      class="size-5 text-primary-400 shrink-0"
    />
    <p class="text-sm tint-primary">
      Your {{ currentPlanConfig.label }} trial ends in <strong>{{ trialDaysRemaining }} day{{ trialDaysRemaining === 1 ? '' : 's' }}</strong>.
    </p>
  </div>

  <!-- Banner 6: Paid plan cancellation pending -->
  <div
    v-else-if="authStore.cancelAtPeriodEnd && planDaysRemaining !== null"
    class="flex items-center gap-3 px-4 py-3 rounded-xl border border-warning-400/25 bg-warning-500/10 mb-4"
  >
    <UIcon
      name="i-lucide-alert-triangle"
      class="size-5 text-warning-400 shrink-0"
    />
    <p class="text-sm text-warning-200">
      Your subscription is set to cancel on <strong>{{ periodEndFormatted }}</strong>.
      You'll keep your {{ currentPlanConfig.label }} features
      <template v-if="planDaysRemaining > 0">
        for <strong>{{ planDaysRemaining }} more day{{ planDaysRemaining === 1 ? '' : 's' }}</strong>.
      </template>
      <template v-else>
        until the end of today.
      </template>
    </p>
  </div>

  <!-- Current plan card -->
  <UPageCard
    variant="subtle"
    class="border border-default [&>div]:py-4!"
  >
    <div class="flex flex-col sm:flex-row sm:items-center gap-4">
      <div class="flex items-center gap-3">
        <div
          class="flex items-center justify-center size-10 rounded-xl"
          :class="{
            'bg-elevated/50': authStore.plan === 'free',
            'bg-fuchsia-500/15 border border-fuchsia-400/25': authStore.plan === 'pro',
            'bg-warning-500/15 border border-warning-400/25': authStore.plan === 'elite'
          }"
        >
          <UIcon
            :name="currentPlanConfig.icon"
            class="size-5"
            :class="{
              'text-muted': authStore.plan === 'free',
              'text-fuchsia-400': authStore.plan === 'pro',
              'text-warning-400': authStore.plan === 'elite'
            }"
          />
        </div>
        <div>
          <div class="flex items-center gap-2">
            <h3 class="text-lg font-semibold text-highlighted">
              {{ currentPlanConfig.label }}
            </h3>
            <UBadge
              v-if="statusLabel"
              :color="statusColor"
              variant="subtle"
              size="sm"
            >
              {{ statusLabel }}
            </UBadge>
          </div>
          <p class="text-sm text-muted">
            {{ currentPlanConfig.description }}
          </p>
        </div>
      </div>

      <div
        v-if="authStore.plan !== 'free' && !authStore.isLifetime"
        class="sm:ml-auto"
      >
        <UButton
          label="Manage Subscription"
          icon="i-lucide-external-link"
          color="neutral"
          variant="outline"
          :loading="portalLoading"
          @click="handleManageSubscription"
        />
      </div>
    </div>

    <template
      v-if="authStore.plan !== 'free' && (authStore.isLifetime || periodEndFormatted)"
      #footer
    >
      <p
        v-if="authStore.isLifetime"
        class="text-xs text-success-300 flex items-center gap-1.5"
      >
        <UIcon
          name="i-lucide-infinity"
          class="size-3.5"
        />
        Lifetime access · No expiration · No recurring charges
      </p>
      <p
        v-else
        class="text-xs text-dimmed"
      >
        <template v-if="isTrialCancelled">
          Trial ends: {{ periodEndFormatted }} · Cancelled, no charge
        </template>
        <template v-else-if="authStore.planStatus === 'trialing'">
          Trial ends: {{ periodEndFormatted }}
        </template>
        <template v-else-if="authStore.cancelAtPeriodEnd">
          Features available until: {{ periodEndFormatted }}
        </template>
        <template v-else>
          Next billing date: {{ periodEndFormatted }}
        </template>
      </p>
    </template>
  </UPageCard>

  <!-- Plan comparison -->
  <UPageCard
    title="Plans"
    description="Choose the plan that fits your needs."
    variant="naked"
    orientation="horizontal"
    class="mt-8 mb-4"
  >
    <!-- Billing cycle + currency toggle -->
    <div class="flex items-center gap-3 lg:ms-auto">
      <div
        v-if="!isOnetime"
        class="flex items-center gap-1"
      >
        <button
          class="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
          :class="billingCycle === 'monthly' ? 'bg-elevated text-highlighted' : 'text-muted hover:text-toned'"
          @click="billingCycle = 'monthly'"
        >
          Monthly
        </button>
        <button
          class="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
          :class="billingCycle === 'yearly' ? 'bg-elevated text-highlighted' : 'text-muted hover:text-toned'"
          @click="billingCycle = 'yearly'"
        >
          Yearly
          <span
            class="text-xs text-green-400 font-semibold"
          >Save up to 25%</span>
        </button>
      </div>
      <div
        v-else
        class="text-xs text-muted"
      >
        Pay once · Lifetime access
      </div>
      <div class="w-px h-5 bg-elevated" />
      <div class="flex items-center gap-0.5 p-0.5 rounded-lg bg-elevated/50 border border-default">
        <button
          v-for="c in currencies"
          :key="c"
          class="px-2 py-1 rounded-md text-xs font-medium transition-all duration-200"
          :class="currency === c
            ? 'bg-elevated text-highlighted'
            : 'text-dimmed hover:text-muted'"
          @click="setCurrency(c)"
        >
          {{ c }}
        </button>
      </div>
    </div>
  </UPageCard>

  <PlanCards
    :plans="plansForMode"
    :billing-cycle="billingCycle"
    :checkout-loading="checkoutLoading"
    :get-button-label="getPlanButtonLabel"
    :payment-mode="paymentMode"
    @plan-action="handleCheckout"
  />

  <!-- Add-ons -->
  <UPageCard
    title="Add-ons"
    description="Standalone extras billed monthly. They work with every plan, including Free."
    variant="naked"
    orientation="horizontal"
    class="mt-8 mb-4"
  />

  <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
    <AddonCard addon="custom_domain" />
  </div>
</template>
